import type React from 'react'
import { useMemo, useState, useRef, useEffect } from 'react'
import { Spin, Select, Input, Space, InputNumber } from 'antd'
import { useBulkOrderUpdate } from '@api/useBulkOrderUpdate'
import type { JobLogEntry, LogLevel } from '@api/useBulkOrderUpdate/types'
import { SearchOutlined } from '@ant-design/icons'
import { formatters } from '@utils/intl/dateTimeFormatters'
import ScrollToBottom from 'react-scroll-to-bottom'

interface BulkOrderJobsLogsProps {
  jobId: string | null
}

export const BulkOrderJobsLogs: React.FC<BulkOrderJobsLogsProps> = ({ jobId }) => {
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('INFO')
  const [searchFilter, setSearchFilter] = useState('')
  const [contextLines, setContextLines] = useState(0)
  const [selectedLogIndex, setSelectedLogIndex] = useState<number | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Log4j style hierarchy (higher index = higher severity)
  const logLevelHierarchy = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'WARNING', 'ERROR']

  const getMinimumLevelIndex = (selectedLevel: LogLevel | 'all'): number => {
    if (selectedLevel === 'all') return -1
    const index = logLevelHierarchy.indexOf(selectedLevel.toUpperCase())
    return index >= 0 ? index : -1
  }

  const { useGetJobQuery } = useBulkOrderUpdate()
  const { data: job, isLoading } = useGetJobQuery(jobId || undefined)

  const matchesSearch = (log: JobLogEntry, searchTerm: string) => {
    const search = searchTerm.toLowerCase()
    return (
      log.message.toLowerCase().includes(search) ||
      (log.order_number && log.order_number.toString().toLowerCase().includes(search))
    )
  }

  const filteredLogs = useMemo(() => {
    if (!job?.logs) return []

    const minimumLevelIndex = getMinimumLevelIndex(levelFilter)

    // First pass: filter by level, keeping track of original indices
    const levelFiltered: { log: JobLogEntry; originalIndex: number }[] = []
    job.logs.forEach((log: JobLogEntry, originalIndex: number) => {
      if (levelFilter !== 'all') {
        const logLevelIndex = logLevelHierarchy.indexOf(log.level.toUpperCase())
        if (logLevelIndex === -1 || logLevelIndex < minimumLevelIndex) {
          return
        }
      }
      levelFiltered.push({ log, originalIndex })
    })

    // If no search filter, return level-filtered logs
    if (!searchFilter) {
      return levelFiltered.map(({ log, originalIndex }) => ({ log, originalIndex, isMatch: false, isContext: false }))
    }

    // Find matches
    const matches = new Set<number>()
    levelFiltered.forEach(({ log }, filteredIndex) => {
      if (matchesSearch(log, searchFilter)) {
        matches.add(filteredIndex)
      }
    })

    // Add context lines
    const includedIndices = new Set<number>()
    matches.forEach((matchIndex) => {
      for (
        let i = Math.max(0, matchIndex - contextLines);
        i <= Math.min(levelFiltered.length - 1, matchIndex + contextLines);
        i++
      ) {
        includedIndices.add(i)
      }
    })

    // Build final result with match/context flags
    return Array.from(includedIndices)
      .sort((a, b) => a - b)
      .map((filteredIndex) => ({
        log: levelFiltered[filteredIndex].log,
        originalIndex: levelFiltered[filteredIndex].originalIndex,
        isMatch: matches.has(filteredIndex),
        isContext: !matches.has(filteredIndex),
      }))
  }, [job?.logs, levelFilter, searchFilter, contextLines, logLevelHierarchy])

  const clearFilters = () => {
    setLevelFilter('INFO')
    setSearchFilter('')
    setContextLines(0)
    // Scroll to selected log if one is selected
    if (selectedLogIndex !== null && scrollContainerRef.current) {
      setTimeout(() => {
        const logElement = scrollContainerRef.current?.querySelector(`[data-original-index="${selectedLogIndex}"]`)
        if (logElement) {
          logElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }

  return (
    <div className='h-full'>
      <div className='mb-3'>
        <Space wrap>
          <Select
            value={levelFilter}
            onChange={setLevelFilter}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'TRACE', label: 'Trace' },
              { value: 'DEBUG', label: 'Debug' },
              { value: 'INFO', label: 'Info' },
              { value: 'WARN', label: 'Warn' },
              { value: 'ERROR', label: 'Error' },
            ]}
          />
          <Input
            placeholder='Search logs and order numbers...'
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            style={{ width: 250 }}
            prefix={<SearchOutlined />}
          />
          {searchFilter && (
            <>
              <span style={{ color: '#666' }}>Context:</span>
              <InputNumber
                value={contextLines}
                onChange={(value) => setContextLines(value || 0)}
                min={0}
                max={10}
                style={{ width: 80 }}
                addonBefore='±'
              />
            </>
          )}
          <button
            onClick={clearFilters}
            style={{
              padding: '4px 8px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        </Space>
      </div>

      <div className='console-container h-full'>
        <div
          className='console-window flex-1'
          style={isLoading ? { display: 'flex', justifyContent: 'center', alignItems: 'center' } : {}}
        >
          {isLoading ? (
            <Spin size='large' />
          ) : (
            <LogDisplay
              logs={filteredLogs}
              jobId={jobId}
              searchTerm={searchFilter}
              selectedLogIndex={selectedLogIndex}
              onLogSelect={setSelectedLogIndex}
              scrollContainerRef={scrollContainerRef}
            />
          )}
        </div>
      </div>
    </div>
  )
}

interface LogDisplayProps {
  logs: { log: JobLogEntry; originalIndex: number; isMatch: boolean; isContext: boolean }[]
  jobId: string | null
  searchTerm: string
  selectedLogIndex: number | null
  onLogSelect: (index: number) => void
  scrollContainerRef: React.RefObject<HTMLDivElement>
}

const LogDisplay: React.FC<LogDisplayProps> = ({
  logs,
  jobId,
  searchTerm,
  selectedLogIndex,
  onLogSelect,
  scrollContainerRef,
}) => {
  if (!jobId) {
    return (
      <div className='p-4 text-white text-center flex items-center justify-center h-full'>
        Select a job to view logs
      </div>
    )
  }

  if (!logs || logs.length === 0) {
    return (
      <div className='p-4 text-white text-center flex items-center justify-center h-full'>
        No logs available for this job
      </div>
    )
  }

  return (
    <ScrollToBottom className='console-scroll h-full' ref={scrollContainerRef}>
      {logs.map((logData, index) => (
        <LogEntry
          key={`${logData.originalIndex}-${index}`}
          log={logData.log}
          isMatch={logData.isMatch}
          isContext={logData.isContext}
          searchTerm={searchTerm}
          isSelected={selectedLogIndex === logData.originalIndex}
          onClick={() => onLogSelect(logData.originalIndex)}
          dataOriginalIndex={logData.originalIndex}
        />
      ))}
    </ScrollToBottom>
  )
}

interface LogEntryProps {
  log: JobLogEntry
  isMatch?: boolean
  isContext?: boolean
  searchTerm?: string
  isSelected?: boolean
  onClick?: () => void
  dataOriginalIndex?: number
}

const LogEntry: React.FC<LogEntryProps> = ({
  log,
  isMatch,
  isContext,
  searchTerm,
  isSelected,
  onClick,
  dataOriginalIndex,
}) => {
  const getLevelStyle = (level: LogLevel) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return { color: '#ef4444' } // red-500
      case 'WARNING':
      case 'WARN':
        return { color: '#eab308' } // yellow-500
      case 'DEBUG':
        return { color: '#facc15' } // yellow-400
      case 'TRACE':
        return { color: '#a78bfa' } // violet-400
      case 'INFO':
        return { color: '#ffffff' } // white
      default:
        return { color: '#9ca3af' } // gray-400
    }
  }

  const formatLocalTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatLevel = (level: LogLevel) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return '🔴'
      case 'WARNING':
      case 'WARN':
        return '🟡'
      case 'DEBUG':
        return '🔍'
      case 'TRACE':
        return '🔬'
      case 'INFO':
        return '\u00A0\u00A0'
      default:
        return level.charAt(0).toUpperCase()
    }
  }
  const highlightSearchTerm = (text: string, term: string) => {
    if (!term) return text
    const regex = new RegExp(`(${term})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: '#facc15', color: '#000' }}>
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  const getEntryStyle = () => {
    const style: React.CSSProperties = {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    }

    if (isSelected) {
      style.backgroundColor = '#1f2937' // darker background for selected
      style.borderLeft = '3px solid #3b82f6' // blue left border
    }

    if (isContext && !isMatch) {
      style.opacity = 0.7
      style.borderLeft = '2px solid #6b7280' // gray left border for context
    }

    return style
  }

  return (
    <div
      className='log-entry mb-1 font-mono text-sm flex'
      style={getEntryStyle()}
      onClick={onClick}
      data-original-index={dataOriginalIndex}
    >
      <span className='console-time text-gray-500 w-20 flex-shrink-0'>{formatLocalTime(log.timestamp)}</span>
      <span className='ml-3 font-bold w-20 flex-shrink-0' style={getLevelStyle(log.level)}>
        {formatLevel(log.level)}
      </span>
      {log.order_number && (
        <span className='ml-3 text-cyan-400 flex-shrink-0'>
          [Order:{' '}
          <a
            target='_blank'
            href={'/order_overview_page/all?order_number=' + log.order_number}
            style={{
              color: '#67e8f9',
              textDecoration: 'none',
              cursor: 'pointer',
            }} rel="noreferrer"
          >
            {searchTerm ? highlightSearchTerm(log.order_number.toString(), searchTerm) : log.order_number}
          </a>
          ]
        </span>
      )}
      <span className='ml-3 text-gray-200 flex-1'>
        {searchTerm ? highlightSearchTerm(log.message, searchTerm) : log.message}
      </span>
      {log.details && <div className='ml-8 text-gray-600 text-xs mt-1'>{JSON.stringify(log.details, null, 2)}</div>}
    </div>
  )
}
